import DataModel from "../../model-factory/basic/data/DataModel";
import {HttpMethod} from "../../common/SimpleType";
import ApiResponseVO from "./ApiResonseVO";
import ApiError, {determineApiError} from "./ApiError";
import {getManager} from "../Manager";
import {NavigationLevelVO} from "../navigation/NavigationModel";
import ApiRequestVO from "./ApiRequestVO";
import {IAnyObject, IStringObject} from "../../common/SimpleInterface";
import {isArray, isBool, isNativeObject, isNumber, isString, trimSlashes} from "../../common/SiteHelper";
import {dispatchFetchRequestStateChange, dispatchInternetConnectionUpdate} from "../../event/DispatchEvent";

export type ContentTypeID = "json" | "none";

export default class ApiManager
{
    apiHome:string = "";
    connectionTestImage:string = "";
    hasNoInternetConnection:boolean = false;
    fetchCount: number = 0;
    populateCount: number = 0;
    openFetchRequestIDs:number[] = [];
    activeLevelList: NavigationLevelVO[] = [];
    testNoInternet:boolean = false;

    delay(ms: number)
    {
        return new Promise( resolve => setTimeout(resolve, ms) );
    }

    isTestNoInternet()
    {
        return (this.testNoInternet || getManager().app.noServerAvailable) && getManager().app.appDebug;
    }

    hasOpenFetchRequest(): boolean
    {
        return !!this.openFetchRequestIDs.length;
    }

    async fetchData(endpoint: string, httpMethod:HttpMethod, params?:IAnyObject|BodyInit|null, includeAuthorization:boolean|string = false, contentTypeID:ContentTypeID|null = null): Promise<ApiResponseVO>
    {
        contentTypeID = contentTypeID ? contentTypeID : "json"

        endpoint = trimSlashes(endpoint);
        const isPrimaryAPI = !endpoint.startsWith("http://") && !endpoint.startsWith("https://")
        if(isPrimaryAPI){
            let apiHome= this.apiHome;
            if(this.isTestNoInternet()){
                apiHome = "http://www.debugfakeurl.com";
            }
            endpoint = apiHome + "/" + endpoint;
        }
        let bodyData:any = null;
        if(httpMethod === "GET"){
            endpoint = this.addQueryParams(endpoint, params as IAnyObject);
        } else {
            if(params){
                bodyData = contentTypeID === "json" ? JSON.stringify(params) : (params as BodyInit);
            }
        }

        const headers:HeadersInit = {
            'Connection': 'keep-alive'
        }
        if(contentTypeID === "json"){
            headers['Content-Type'] = 'application/json; charset=UTF-8';
        }

        includeAuthorization = includeAuthorization === true ? (getManager().user.loginToken ? "Bearer " + getManager().user.loginToken : false) : includeAuthorization;

        if(includeAuthorization){
            headers.Authorization = includeAuthorization;
        }

        let fetchResponseVO:ApiResponseVO|null = null;

        let applyNoInternet = () => {
            if(!fetchResponseVO){
                fetchResponseVO = new ApiResponseVO();
                fetchResponseVO.failedToLoad = true;
                fetchResponseVO.endpoint = endpoint;
            }
            if(!fetchResponseVO.apiError){
                fetchResponseVO.apiError = new ApiError();
            }
            fetchResponseVO.apiError.status = 503;
            fetchResponseVO.apiError.code = "no_internet_connection";
            fetchResponseVO.apiError.message = "No internet connection";

            fetchResponseVO.hasNoInternetConnection = true;
            const hadNoInternetConnection = this.hasNoInternetConnection;
            this.hasNoInternetConnection = true;
            dispatchInternetConnectionUpdate(true, hadNoInternetConnection);
            return fetchResponseVO;
        }

        if(isPrimaryAPI && getManager().app.noServerAvailable){
            return applyNoInternet();
        }

        this.fetchCount ++;
        const fetchRequestID = this.fetchCount;
        this.openFetchRequestIDs.push(fetchRequestID);
        dispatchFetchRequestStateChange();
        const removeOpenFetchRequestID = () => {
            const index = this.openFetchRequestIDs.indexOf(fetchRequestID);
            if(index >= 0){
                this.openFetchRequestIDs.splice(index, 1);
            }
            dispatchFetchRequestStateChange();
        }
        let fetchResponse:Response|null = null;
        fetchResponseVO = await fetch(endpoint, {
            method: httpMethod,
            headers: headers,
            body: bodyData,
        })
            .then((response) =>
            {
                fetchResponse = response;
                return response.json();
            })
            .then((response) => {
                removeOpenFetchRequestID();
                if(this.hasNoInternetConnection){
                    this.hasNoInternetConnection = false;
                    dispatchInternetConnectionUpdate();
                }
                if(response){
                    if(!isNativeObject(response)){
                        if(isArray(response) && response.length){
                            response = {values: response};
                        } else {
                            response = {};
                        }
                    }
                } else {
                    response = {};
                }
                response.fetch_response = {
                    status: fetchResponse?.status ? fetchResponse?.status : null,
                    statusText: fetchResponse?.statusText ? fetchResponse?.statusText : null
                };
                let hasError = false;

                let data = isNativeObject(response.data) ? response.data : {};
                if(data.data && isNativeObject(data.data) && response.hasOwnProperty("success") ){
                    hasError = !response.success;
                    response = data;
                }

                const responseVO = new ApiResponseVO();
                responseVO.response = response ? response : null;
                responseVO.endpoint = endpoint;
                responseVO.apiError = determineApiError(response, hasError);
                if(!responseVO.apiError){
                    responseVO.message = isString(response.message) ? response.message : "";
                    responseVO.data = response;
                } else {
                    if(responseVO.apiError.status === 403){
                        responseVO.invalidCredentials = true;
                        if(responseVO.apiError.code !== "missing_capability"){
                            responseVO.invalidLogin = true;
                        }
                    }
                }

                console.log(httpMethod+" "+endpoint)
                console.log({params: params, response: response})

                return responseVO;
            })
            .catch((error) => {
                removeOpenFetchRequestID();
                const responseVO = new ApiResponseVO();
                responseVO.response = error ? error : null;
                responseVO.endpoint = endpoint;
                responseVO.apiError = new ApiError(404, error ? error : "Failed to load response data")
                responseVO.failedToLoad = true;
                return responseVO;
            });

        if(!fetchResponseVO.failedToLoad){
            return fetchResponseVO;
        }

        if(this.hasNoInternetConnection){
            applyNoInternet();
            return fetchResponseVO;
        }

        return await this.testInternetConnection().then((hasInternetConnection) => {
            if(!hasInternetConnection){
                applyNoInternet();
            }
            return fetchResponseVO ?? new ApiResponseVO();
        })
    }

    async testInternetConnection(){
        return new Promise((resolve) => {
            if(!this.connectionTestImage){
                resolve(true);
                return;
            }

            if(this.isTestNoInternet()){
                resolve(false);
                return;
            }

            let img = new Image()
            img.onload = () => {
                resolve(true)
            }
            img.onerror = () => {
                resolve(false)
            }
            img.src = this.connectionTestImage +'?ts' + Date.now()
        })
    }

    addQueryParams(endpoint: string, params?:IAnyObject|null): string
    {
        if(!params){
            return endpoint;
        }
        let useAnd = endpoint.includes("?");
        for(let key in params){
            const value = params[key];
            const useValue = this.prepareQueryValue(value);
            if(useValue === undefined){
                continue;
            }
            endpoint += !useAnd ? "?" : "&";
            endpoint += key + "=" + encodeURIComponent(useValue);
            useAnd = true;
        }
        return endpoint;
    }

    prepareQueryValue(value?:any): string|undefined
    {
        let useValue:any = undefined;
        if(value === undefined){
            return useValue;
        }
        if(isString(value)){
            return value;
        } else if(isNumber(value)){
            return value.toString();
        } else if(isBool(value)){
            return value ? "1" : "";
        } else if (value) {
            const jsonValue = JSON.stringify(value);
            if(jsonValue){
                return jsonValue;
            }
        }
        return useValue;
    }


    getUnpopulatedDataModels(levelList: NavigationLevelVO[], withValidRequestVO:boolean = false): DataModel[]
    {
        const dataModels: DataModel[]  = [];

        for(let i = 0; i < levelList.length; i ++)
        {
            const derivedModelList = levelList[i].derivedModels;

            for (let j = derivedModelList.length - 1; j >= 0; j--)
            {
                const model = derivedModelList[j];
                if (model instanceof DataModel)
                {
                    if(dataModels.includes(model) || model.isPopulated()){
                        continue;
                    }
                    if(withValidRequestVO){

                        const requestVO: ApiRequestVO|null = model.getPopulateRequestVO();
                        if(!requestVO || !requestVO.endpoint){
                            continue
                        }
                    }
                    dataModels.push(model);
                }
            }
        }
        return dataModels
    }

    async requirePopulateData(onDelay: (() => void) ): Promise<boolean>
    {
        this.populateCount ++;
        const currentPopulateCount = this.populateCount;

        const levelList = getManager().navigation.model.levelList.slice();
        this.activeLevelList = levelList;

        const dataModels: DataModel[]  = this.getUnpopulatedDataModels(levelList);
        const requestVOs: ApiRequestVO[]  = [];
        for(let i = 0; i < dataModels.length; i++)
        {
            const dataModel = dataModels[i];
            const requestVO: ApiRequestVO|null = dataModel.getPopulateRequestVO();
            if(!requestVO || !requestVO.endpoint){
                await dataModel.handlePopulated()
                dataModels.splice(i, 1);
                i --;
                continue;
            }
            requestVO.dataModel = dataModel;
            requestVOs.push(requestVO);
        }

        if(!requestVOs.length){
            return false;
        }
        onDelay();

        return this.requestMultiplePopulateData(requestVOs).then((_) => {
            if(currentPopulateCount !== this.populateCount){
                return false;
            }

            this.activeLevelList = [];
            return true;
        });
    }

    async requestMultiplePopulateData(requestVOs:ApiRequestVO[]) :Promise<ApiError[]> {
        const apiErrors:ApiError[] = []
        return await Promise.all(requestVOs.map(async (requestVO) => {
            await this.requestPopulateData(requestVO).then((error:ApiError|null) => {
                if(error){
                    apiErrors.push(error)
                }
            });
        })).then(() => {
            return apiErrors;
        });
    }

    async requestPopulateData(requestVO:ApiRequestVO) :Promise<ApiError|null> {

        const endpoint:string = requestVO.endpoint;
        const httpMethod:HttpMethod = requestVO.httpMethod;
        const params:IAnyObject = JSON.parse(JSON.stringify(requestVO.params));
        if(requestVO.dataModel){
            requestVO.dataModel.populating = true;
            requestVO.dataModel.onDataModelChanged();
        }

        return getManager().api.fetchData(endpoint, httpMethod, params, requestVO.includeAuthorization).then(responseVO => {
            const dataModel = requestVO.dataModel;
            if(!dataModel){
                return responseVO.apiError ?? new ApiError(0, "no_data_model", "No Data Model");
            }
            return dataModel.onRequestResponseReceived(responseVO, !requestVO.isAdditionalData).then(() => {
                return dataModel.handlePopulated().then(() => {
                    return null;
                });
            }).catch((apiError:ApiError) => {
                return dataModel.handlePopulated(apiError).then(() => {
                    return apiError;
                });
            });
        })
    }

    async requestData(requestVO:ApiRequestVO) :Promise<ApiResponseVO> {
        const endpoint:string = requestVO.endpoint;
        const httpMethod:HttpMethod = requestVO.httpMethod;
        const params:IAnyObject = JSON.parse(JSON.stringify(requestVO.params));
        return getManager().api.fetchData(endpoint, httpMethod, params, requestVO.includeAuthorization);
    }
}